import { StaderEthxWithdrawalAbi } from '../abis/staderEthx'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Stader ETHx — per-user request ID list + per-id detail.
 *
 * Two stages:
 *  1. `getRequestIdsByUser(owner)` → uint256[] of request IDs.
 *  2. `userWithdrawRequests(id)` per id → request struct. Status is
 *     finalized iff `id < nextRequestIdToFinalize` AND
 *     `ethFinalized > 0`.
 *
 * `amountUnderlying` uses `ethFinalized` when finalized (the actual
 * payable amount) and `ethExpected` while pending.
 */
export const readerStaderEthx: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getRequestIdsByUser',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'nextRequestIdToFinalize',
          params: [],
        },
      ] as unknown as never[],
      abi: [
        StaderEthxWithdrawalAbi,
        StaderEthxWithdrawalAbi,
      ] as unknown as never,
    })) as unknown[]
    const ids = stage1[0] as unknown[] | undefined
    const frontier = toBigInt(stage1[1]) ?? 0n
    if (!Array.isArray(ids) || ids.length === 0) return []

    const idBigInts = ids
      .map(toBigInt)
      .filter((v): v is bigint => v !== undefined)

    const detailCalls = idBigInts.map((id) => ({
      address: entry.withdrawalContract!,
      name: 'userWithdrawRequests',
      params: [id],
    }))
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls as unknown as never[],
      abi: detailCalls.map(() => StaderEthxWithdrawalAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < idBigInts.length; i++) {
      const id = idBigInts[i]
      const tup = stage2[i] as unknown[] | undefined
      if (!Array.isArray(tup) || tup.length < 5) continue
      const ethExpected = toBigInt(tup[2]) ?? 0n
      const ethFinalized = toBigInt(tup[3]) ?? 0n
      const isFinalized = id < frontier && ethFinalized > 0n
      const queuePosition = isFinalized ? 0 : Number(id - frontier)
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: id.toString(),
        amountUnderlying: (isFinalized ? ethFinalized : ethExpected).toString(),
        status: isFinalized ? 'claimable' : 'pending',
        queuePosition: queuePosition > 0 ? queuePosition : undefined,
      })
    }
    return out
  },
}
